/**
 * Which file the prefetcher fetches next, as a pure function of what
 * is on disk and where the viewer is looking.
 *
 * Keeping the decision pure means the walker holds no queue that can
 * drift out of step with reality: the playhead moves, the player's own
 * streaming writes entries through the same cache, and every call sees
 * the current truth rather than a plan made minutes ago.
 *
 * Baseline order, per docs/LOCAL-MEDIA-CACHE.md:
 *   1. Active angle, playhead forward to the end.
 *   2. Active angle, start of programme forward to the playhead.
 *   3. Standby angles, in presentation order, same two passes.
 *   4. Audio tracks.
 *   5. Posters, as a closing sweep.
 *
 * After a forward seek the hunting heuristic takes over instead, until
 * the skipped region is closed.
 */

export interface SegmentItem {
  kind: 'segment';
  /** Canonical cache key: origin and path, no signature. */
  key: string;
  /** Signed URL; replaced wholesale when signatures are refreshed. */
  url: string;
  angleId: string;
  index: number;
  startSeconds: number;
  endSeconds: number;
}

export interface PosterItem {
  kind: 'poster';
  key: string;
  url: string;
  angleId: string;
}

export interface TrackItem {
  kind: 'track';
  key: string;
  url: string;
  trackId: string;
}

export type CacheItem = SegmentItem | PosterItem | TrackItem;

/**
 * A viewer who jumps forward is searching, and the next jump is more
 * likely backwards from where they landed than anywhere else. The
 * skipped region is therefore closed from both ends at once: backwards
 * from the target, forwards from the departure point.
 */
export interface HuntWindow {
  originSeconds: number;
  targetSeconds: number;
}

export interface PrefetchState {
  /** Angle order as the manifest presents it; the first is the default. */
  angleIds: readonly string[];
  activeAngleId: string;
  playheadSeconds: number;
  hunt: HuntWindow | null;
  /** Which end of the hunt window gets the next fetch. */
  turn: 'backward' | 'forward';
  /** Canonical keys already on disk, plus any permanently skipped. */
  unavailable: ReadonlySet<string>;
}

function segmentsOf(items: readonly CacheItem[], angleId: string): SegmentItem[] {
  const segments: SegmentItem[] = [];
  for (const item of items) {
    if (item.kind === 'segment' && item.angleId === angleId) {
      segments.push(item);
    }
  }
  segments.sort((a, b) => a.index - b.index);
  return segments;
}

function firstMissing(
  candidates: readonly CacheItem[],
  unavailable: ReadonlySet<string>,
): CacheItem | null {
  for (const item of candidates) {
    if (!unavailable.has(item.key)) {
      return item;
    }
  }
  return null;
}

/**
 * Segments overlapping the skipped region, including the two that
 * carry its ends: those are the segments the player must decode from
 * to answer a jump back to either edge.
 */
function huntBand(segments: readonly SegmentItem[], hunt: HuntWindow): SegmentItem[] {
  const low = Math.min(hunt.originSeconds, hunt.targetSeconds);
  const high = Math.max(hunt.originSeconds, hunt.targetSeconds);
  return segments.filter((segment) => segment.startSeconds <= high && segment.endSeconds > low);
}

export function isHuntComplete(items: readonly CacheItem[], state: PrefetchState): boolean {
  if (!state.hunt) {
    return true;
  }
  const band = huntBand(segmentsOf(items, state.activeAngleId), state.hunt);
  return firstMissing(band, state.unavailable) === null;
}

export function chooseNext(items: readonly CacheItem[], state: PrefetchState): CacheItem | null {
  if (state.hunt) {
    const band = huntBand(segmentsOf(items, state.activeAngleId), state.hunt);
    const backwards = [...band].reverse();
    const first = state.turn === 'backward' ? backwards : band;
    const second = state.turn === 'backward' ? band : backwards;
    const picked = firstMissing(first, state.unavailable) ?? firstMissing(second, state.unavailable);
    if (picked) {
      return picked;
    }
    // The region is closed; the baseline order resumes below.
  }

  const ordered: string[] = [
    state.activeAngleId,
    ...state.angleIds.filter((id) => id !== state.activeAngleId),
  ];

  for (const angleId of ordered) {
    const segments = segmentsOf(items, angleId);
    const ahead = segments.filter((segment) => segment.endSeconds > state.playheadSeconds);
    const behind = segments.filter((segment) => segment.endSeconds <= state.playheadSeconds);
    const picked =
      firstMissing(ahead, state.unavailable) ?? firstMissing(behind, state.unavailable);
    if (picked) {
      return picked;
    }
  }

  const tracks = items.filter((item): item is TrackItem => item.kind === 'track');
  const track = firstMissing(tracks, state.unavailable);
  if (track) {
    return track;
  }

  const posters = items.filter((item): item is PosterItem => item.kind === 'poster');
  return firstMissing(posters, state.unavailable);
}
